"use client"
import { useState, useEffect, useCallback, useRef } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { cn } from "@/lib/utils"
import { CalendarPlus, Menu, Phone, CircleUserRound, ChevronRight } from "lucide-react"
import { X } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from "@/components/ui/drawer"
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { BookingFlowContent, BookingMode } from "@/components/booking-flow"
import { LoginIframeContent } from "@/components/login-iframe-content"
import { trackEvent } from "@/lib/analytics"
import { isNoosaClinicBooking, isMaroochydoreClinicBooking, isFreeConsultBooking, getPillarFromSource, isExtendedAppointmentBooking, isGeneralHealthInitialBooking, isNoosaReturnAppointmentBooking, isMaroochydoreReturnAppointmentBooking } from "@/lib/booking-constants"

export function Navigation() {
  const [isVisible, setIsVisible] = useState(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isBookingOpen, setIsBookingOpen] = useState(false)
  const [bookingMode, setBookingMode] = useState<BookingMode>({ type: "default" })
  const [isLoginOpen, setIsLoginOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [isMoreOpen, setIsMoreOpen] = useState(false)
  const [isWhatWeHelpWithOpen, setIsWhatWeHelpWithOpen] = useState(false)
  const [isWhatWeHelpWithMobileOpen, setIsWhatWeHelpWithMobileOpen] = useState(false)
  const [isClinicsOpen, setIsClinicsOpen] = useState(false)
  const [isClinicsMobileOpen, setIsClinicsMobileOpen] = useState(false)
  const closeMoreTimer = useRef<number | null>(null)
  const closeWhatWeHelpWithTimer = useRef<number | null>(null)
  const closeClinicsTimer = useRef<number | null>(null)
  const location = useLocation()
  const navigate = useNavigate()

  const whatWeHelpWithPages = [
    { href: "/what-we-help-with/fertility-preconception", label: "Preconception Care & Fertility Support" },
    { href: "/what-we-help-with/pregnancy-postpartum", label: "Pregnancy & Postpartum Support" },
    { href: "/what-we-help-with/menstrual-hormonal-health", label: "Menstrual & Hormonal Health" },
    { href: "/what-we-help-with/stress-sleep-digestion", label: "Stress, Sleep & Digestive Balance" },
    { href: "/what-we-help-with/womens-health-long-term-care", label: "Ongoing Women's Health & Long-Term Care" },
    { href: "/what-we-help-with/other-concerns", label: "Other Concerns" },
  ]

  const clinicsPages = [
    { href: "/clinics/noosa", label: "Noosa Clinic" },
    { href: "/clinics/maroochydore", label: "Maroochydore Clinic" },
  ]

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY
      // Show navigation after scrolling 100px
      setIsVisible(scrollPosition > 100)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 1024) // lg breakpoint
    }
    
    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)
    
    return () => window.removeEventListener('resize', checkIsMobile)
  }, [])

  // Expose a global opener so other components (e.g., Welcome section, HowToBookSection) can open the booking modal
  // Accepts an optional source parameter to determine booking mode
  useEffect(() => {
    ;(window as any).openBookingModal = (source?: string) => {
      // Determine booking mode based on source
      // Priority: free_consult > direct_summary (noosa/maroochydore return) > extended > general_health_initial > location (noosa/maroochydore) > pillar > default
      if (isFreeConsultBooking(source)) {
        setBookingMode({ type: "phone_consultation" })
      } else if (isNoosaReturnAppointmentBooking(source)) {
        // Noosa Return - pre-select everything, go directly to summary
        setBookingMode({ type: "direct_summary", location: "noosa", pillarId: "general-health", appointmentType: "return" })
      } else if (isMaroochydoreReturnAppointmentBooking(source)) {
        // Maroochydore Return - pre-select everything, go directly to summary
        setBookingMode({ type: "direct_summary", location: "maroochydore", pillarId: "general-health", appointmentType: "return" })
      } else if (isExtendedAppointmentBooking(source)) {
        // Extended appointment - pre-select General Health pillar + Extended appointment type
        // User only needs to select location, then goes directly to summary
        setBookingMode({ type: "appointment", pillarId: "general-health", appointmentType: "extended" })
      } else if (isGeneralHealthInitialBooking(source)) {
        // General Health Initial - pre-select General Health pillar + Initial appointment type
        // User only needs to select location, then goes directly to summary
        setBookingMode({ type: "appointment", pillarId: "general-health", appointmentType: "initial" })
      } else if (isNoosaClinicBooking(source)) {
        setBookingMode({ type: "location", location: "noosa" })
      } else if (isMaroochydoreClinicBooking(source)) {
        setBookingMode({ type: "location", location: "maroochydore" })
      } else {
        const pillarId = getPillarFromSource(source)
        if (pillarId) {
          setBookingMode({ type: "pillar", pillarId })
        } else {
          setBookingMode({ type: "default" })
        }
      }
      setIsBookingOpen(true)
    }
    return () => {
      try { delete (window as any).openBookingModal } catch {}
    }
  }, [])

  // Check for URL parameter to open booking modal (e.g., ?book=true or ?booking=true)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const bookParam = searchParams.get('book') || searchParams.get('booking')
    
    if (bookParam === 'true' || bookParam === '1') {
      setIsBookingOpen(true)
      
      // Clean up URL parameter to keep URL clean
      const newSearchParams = new URLSearchParams(location.search)
      newSearchParams.delete('book')
      newSearchParams.delete('booking')
      newSearchParams.delete('source')
      const newSearch = newSearchParams.toString()
      const newUrl = newSearch 
        ? `${location.pathname}?${newSearch}` 
        : location.pathname
      navigate(newUrl, { replace: true })
    }
  }, [location.search, location.pathname, navigate])

  const handleBookingClick = useCallback(() => {
    setIsBookingOpen(true)
  }, [])

  const handleBookingClose = (open: boolean) => {
    setIsBookingOpen(open)
    if (!open) {
      // Reset booking mode when closing
      setBookingMode({ type: "default" })
    }
  }

  const onBookingClick = () => {
    trackEvent('book_appointment_click', { location: 'nav' })
    handleBookingClick()
  }

  const handleLoginClick = useCallback(() => {
    setIsLoginOpen(true)
    setIsDrawerOpen(false)
  }, [])

  const onLoginClick = () => {
    trackEvent('login_click', { location: 'nav' })
    handleLoginClick()
  }

  const navigationLinks = [
    { href: "/dr-renee-knott", label: "Meet Renee" },
    { href: "/about", label: "About Raintree" },
    { href: "more", label: "More..." },
  ]

  // Full list of links for sm/md menus (no "More...")
  const fullMenuLinks = [
    { type: "route" as const, href: "/dr-renee-knott", label: "Meet Renee" },
    { type: "route" as const, href: "/about", label: "About Raintree" },
    { type: "route" as const, href: "/clinics", label: "Clinics" },
    { type: "route" as const, href: "/what-we-help-with", label: "What We Help With" },
    { type: "route" as const, href: "/appointments", label: "Appointments & Fees" },
    { type: "route" as const, href: "/blog", label: "Blog" },
    { type: "anchor" as const, href: "#faq-section", label: "FAQ" },
    { type: "route" as const, href: "/mentoring", label: "Mentoring" },
    { type: "route" as const, href: "/contact", label: "Contact" },
  ]

  const handleNavClick = (href: string) => {
    setIsDrawerOpen(false)
    
    // If it's an anchor link (starts with #)
    if (href.startsWith('#')) {
      // If we're not on the home page, navigate to home with the hash
      if (location.pathname !== '/') {
        // Navigate to home page with hash - ScrollToTop will handle scrolling to the hash
        // Use replace: false to allow back button, and include hash in the path
        navigate({
          pathname: '/',
          hash: href.substring(1) // Remove the # prefix
        })
        return
      }
      
      // We're already on home page, just scroll to the section
      const element = document.querySelector(href)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
      return
    }
    
    // For route links, just navigate normally (ScrollToTop will handle scroll to top)
    navigate(href)
  }

  return (
    <>
      {/* Desktop Navigation - Top */}
      <div className={cn(
        "hidden lg:block fixed top-0 w-full bg-[#231f20]/80 backdrop-blur-sm z-50 transition-transform duration-300 ease-in-out",
        isVisible ? "translate-y-0" : "-translate-y-full"
      )}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <img
                src="https://tdjwqdpgnmzhfcqsfyjh.supabase.co/storage/v1/object/sign/images/logo/RT-LOGO-Navigation.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8wYzVlMGM1NS1lYzI2LTRkMTYtOTc2MS1iZDQ1MWJhMGJiYjEiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvbG9nby9SVC1MT0dPLU5hdmlnYXRpb24ucG5nIiwiaWF0IjoxNzY3NjgzODA2LCJleHAiOjIwODMwNDM4MDZ9.UnTP2GD3PLPeHNBUE18hmLQHQbtbEKr6FRFOtAP3Cvc"
                alt="Raintree Chinese Medicine"
                className="h-12 w-auto"
              />
            </Link>
            
            {/* Desktop Navigation Links - xl and 2xl */}
            <div className="hidden xl:flex items-center space-x-6">
              {/* Primary links */}
              {navigationLinks.slice(0,2).map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className="text-white hover:text-[#DBB981] transition-colors text-sm font-medium"
                >
                  {link.label}
                </button>
              ))}
              {/* Clinics - direct link for xl screens */}
              <div
                className="relative"
                onMouseEnter={() => {
                  if (closeClinicsTimer.current) {
                    clearTimeout(closeClinicsTimer.current)
                    closeClinicsTimer.current = null
                  }
                  setIsClinicsOpen(true)
                }}
                onMouseLeave={() => {
                  if (closeClinicsTimer.current) {
                    clearTimeout(closeClinicsTimer.current)
                  }
                  closeClinicsTimer.current = window.setTimeout(() => setIsClinicsOpen(false), 200)
                }}
              >
                <Link 
                  to="/clinics" 
                  className="text-white hover:text-[#DBB981] transition-colors text-sm font-medium"
                  onMouseEnter={() => setIsClinicsOpen(true)}
                >
                  Clinics
                </Link>
                {/* Safe hover buffer bridging the trigger and menu */}
                <div
                  className="absolute left-0 right-0 top-full h-2"
                  aria-hidden
                />
                <div
                  className={cn(
                    "absolute left-0 top-full w-56 bg-white text-gray-800 border border-gray-200 rounded-xl shadow-2xl transition-opacity pt-6 pb-6",
                    isClinicsOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
                  )}
                >
                  {clinicsPages.map((page) => (
                    <Link
                      key={page.href}
                      to={page.href}
                      state={{ fromParent: false }}
                      className="block px-4 py-2 hover:bg-gray-50 hover:text-[#DBB981] text-sm transition-colors"
                      onClick={() => setIsClinicsOpen(false)}
                    >
                      {page.label}
                    </Link>
                  ))}
                </div>
              </div>
              {/* What We Help With - direct link for xl screens */}
              <div
                className="relative"
                onMouseEnter={() => {
                  if (closeWhatWeHelpWithTimer.current) {
                    clearTimeout(closeWhatWeHelpWithTimer.current)
                    closeWhatWeHelpWithTimer.current = null
                  }
                  setIsWhatWeHelpWithOpen(true)
                }}
                onMouseLeave={() => {
                  if (closeWhatWeHelpWithTimer.current) {
                    clearTimeout(closeWhatWeHelpWithTimer.current)
                  }
                  closeWhatWeHelpWithTimer.current = window.setTimeout(() => setIsWhatWeHelpWithOpen(false), 200)
                }}
              >
                <Link 
                  to="/what-we-help-with" 
                  className="text-white hover:text-[#DBB981] transition-colors text-sm font-medium"
                  onMouseEnter={() => setIsWhatWeHelpWithOpen(true)}
                >
                  What We Help With
                </Link>
                {/* Safe hover buffer bridging the trigger and menu */}
                <div
                  className="absolute left-0 right-0 top-full h-2"
                  aria-hidden
                />
                <div
                  className={cn(
                    "absolute left-0 top-full w-64 bg-white text-gray-800 border border-gray-200 rounded-xl shadow-2xl transition-opacity pt-6 pb-6",
                    isWhatWeHelpWithOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
                  )}
                >
                  <Link
                    to="/what-we-help-with"
                    className="block px-4 py-2 hover:bg-gray-50 hover:text-[#DBB981] text-sm font-medium border-b border-gray-100 transition-colors"
                    onClick={() => setIsWhatWeHelpWithOpen(false)}
                  >
                    Overview
                  </Link>
                  {whatWeHelpWithPages.map((page) => (
                    <Link
                      key={page.href}
                      to={page.href}
                      state={{ fromParent: false }}
                      className="block px-4 py-2 hover:bg-gray-50 hover:text-[#DBB981] text-sm transition-colors"
                      onClick={() => setIsWhatWeHelpWithOpen(false)}
                    >
                      {page.label}
                    </Link>
                  ))}
                </div>
              </div>
              {/* More submenu */}
              <div
                className="relative"
                onMouseEnter={() => {
                  if (closeMoreTimer.current) {
                    clearTimeout(closeMoreTimer.current)
                    closeMoreTimer.current = null
                  }
                  setIsMoreOpen(true)
                }}
                onMouseLeave={() => {
                  if (closeMoreTimer.current) {
                    clearTimeout(closeMoreTimer.current)
                  }
                  closeMoreTimer.current = window.setTimeout(() => setIsMoreOpen(false), 200)
                }}
              >
                <button
                  className="text-white hover:text-[#DBB981] transition-colors text-sm font-medium"
                  aria-haspopup="menu"
                  aria-expanded={isMoreOpen}
                >
                  More...
                </button>
                {/* Safe hover buffer bridging the trigger and menu */}
                <div
                  className="absolute left-0 right-0 top-full h-2"
                  aria-hidden
                />
                <div
                  className={cn(
                    "absolute left-0 top-full w-56 bg-white text-gray-800 border border-gray-200 rounded-xl shadow-2xl transition-opacity pt-6 pb-6",
                    isMoreOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
                  )}
                >
                  <Link to="/appointments" className="block px-4 py-2 hover:bg-gray-50 hover:text-[#DBB981] transition-colors">Appointments & Fees</Link>
                  <Link to="/blog" className="block px-4 py-2 hover:bg-gray-50 hover:text-[#DBB981] transition-colors">Blog</Link>
                  <Link to="/#faq-section" className="block px-4 py-2 hover:bg-gray-50 hover:text-[#DBB981] transition-colors" onClick={() => handleNavClick('#faq-section')}>FAQ</Link>
                  <Link to="/mentoring" className="block px-4 py-2 hover:bg-gray-50 hover:text-[#DBB981] transition-colors">Mentoring</Link>
                  <Link to="/contact" className="block px-4 py-2 hover:bg-gray-50 hover:text-[#DBB981] transition-colors">Contact</Link>
                </div>
              </div>
            </div>

            {/* Button Group */}
            <div className="flex items-center space-x-3">
              {/* Booking Button */}
              <Button
                onClick={onBookingClick}
                className="h-9 rounded-full bg-[#FFF]/90 hover:bg-[#FFF] hover:text-[#DBB981] text-black transition-colors inline-flex items-center gap-2"
              >
                <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                Book Appointment
              </Button>
              
              {/* Call Button */}
              <Button
                asChild
                variant="outline"
                className="h-9 rounded-full border border-[#FFF]/70 text-[#FFF] bg-transparent hover:bg-transparent hover:text-[#DBB981] transition-colors inline-flex items-center gap-2"
              >
                <a href="tel:+61752409192" onClick={() => trackEvent('call_click', { location: 'nav' })}>
                  <Phone className="h-4 w-4" aria-hidden="true" />
                  Call
                </a>
              </Button>

              {/* User Avatar Button - xl screens only */}
              <div className="hidden xl:block">
                <Button
                  onClick={onLoginClick}
                  variant="ghost"
                  className="h-10 w-10 rounded-full p-0 text-white hover:bg-white/10 hover:text-[#DBB981] transition-colors"
                  aria-label="Login"
                >
                  <CircleUserRound className="h-7 w-7" strokeWidth={1} />
                </Button>
              </div>

              {/* Right-side menu for md screens (Sheet) */}
              <div className="hidden md:block xl:hidden">
                <Sheet open={isDrawerOpen} onOpenChange={(open) => {
                  setIsDrawerOpen(open)
                  if (!open) {
                    setIsWhatWeHelpWithMobileOpen(false)
                    setIsClinicsMobileOpen(false)
                  }
                }}>
                  <SheetTrigger asChild>
                    <button
                      className="w-10 h-10 p-0 flex items-center justify-center text-white bg-transparent hover:bg-transparent focus:outline-none focus:ring-0"
                      aria-label="Open menu"
                    >
                      <Menu className="h-5 w-5 text-white" />
                    </button>
                  </SheetTrigger>
                  <SheetContent side="right" className="bg-white w-full max-w-[350px]" overlayClassName="bg-black/30">
                    <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                    <div className="p-2 mt-2">
                      <div className="flex flex-col gap-1">
                        {fullMenuLinks.map((item) => {
                          if (item.href === "/what-we-help-with") {
                            return (
                              <div key={item.href}>
                                <button
                                  onClick={() => setIsWhatWeHelpWithMobileOpen(!isWhatWeHelpWithMobileOpen)}
                                  className="w-full text-left text-gray-800 hover:text-[#DBB981] transition-colors px-3 py-2 rounded-md hover:bg-gray-50 flex items-center justify-between"
                                >
                                  <span>{item.label}</span>
                                  <ChevronRight className={cn("h-5 w-5 flex-shrink-0 transition-transform", isWhatWeHelpWithMobileOpen ? "rotate-90" : "")} />
                                </button>
                                {isWhatWeHelpWithMobileOpen && (
                                  <div className="ml-4 mt-1 space-y-1">
                                    <Link
                                      to="/what-we-help-with"
                                      className="block text-gray-700 hover:text-[#DBB981] transition-colors px-3 py-2 rounded-md hover:bg-gray-50 text-sm"
                                      onClick={() => setIsDrawerOpen(false)}
                                    >
                                      Overview
                                    </Link>
                                    {whatWeHelpWithPages.map((page) => (
                                      <Link
                                        key={page.href}
                                        to={page.href}
                                        state={{ fromParent: false }}
                                        className="block text-gray-700 hover:text-[#DBB981] transition-colors px-3 py-2 rounded-md hover:bg-gray-50 text-sm"
                                        onClick={() => setIsDrawerOpen(false)}
                                      >
                                        {page.label}
                                      </Link>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )
                          }
                          if (item.href === "/clinics") {
                            return (
                              <div key={item.href}>
                                <button
                                  onClick={() => setIsClinicsMobileOpen(!isClinicsMobileOpen)}
                                  className="w-full text-left text-gray-800 hover:text-[#DBB981] transition-colors px-3 py-2 rounded-md hover:bg-gray-50 flex items-center justify-between"
                                >
                                  <span>{item.label}</span>
                                  <ChevronRight className={cn("h-5 w-5 flex-shrink-0 transition-transform", isClinicsMobileOpen ? "rotate-90" : "")} />
                                </button>
                                {isClinicsMobileOpen && (
                                  <div className="ml-4 mt-1 space-y-1">
                                    {clinicsPages.map((page) => (
                                      <Link
                                        key={page.href}
                                        to={page.href}
                                        state={{ fromParent: false }}
                                        className="block text-gray-700 hover:text-[#DBB981] transition-colors px-3 py-2 rounded-md hover:bg-gray-50 text-sm"
                                        onClick={() => setIsDrawerOpen(false)}
                                      >
                                        {page.label}
                                      </Link>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )
                          }
                          return item.type === "route" ? (
                            <Link
                              key={item.href}
                              to={item.href}
                              className="text-gray-800 hover:text-[#DBB981] transition-colors px-3 py-2 rounded-md hover:bg-gray-50"
                              onClick={() => setIsDrawerOpen(false)}
                            >
                              {item.label}
                            </Link>
                          ) : (
                            <button
                              key={item.href}
                              onClick={() => handleNavClick(item.href)}
                              className="text-left text-gray-800 hover:text-[#DBB981] transition-colors px-3 py-2 rounded-md hover:bg-gray-50"
                            >
                              {item.label}
                            </button>
                          )
                        })}
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <button
                          onClick={onLoginClick}
                          className="w-full text-left text-gray-800 hover:text-[#DBB981] transition-colors px-3 py-2 rounded-md hover:bg-gray-50 flex items-center gap-2"
                        >
                          <CircleUserRound className="h-5 w-5" strokeWidth={1.5} />
                          <span className="text-sm font-semibold text-gray-900">My Account</span>
                        </button>
                      </div>
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation - Top */}
      <div className={cn(
        "lg:hidden fixed top-0 w-full bg-black/70 backdrop-blur-sm z-50 transition-transform duration-300 ease-in-out border-b border-white/20",
        isVisible ? "translate-y-0" : "-translate-y-full"
      )}>
        <div className="container mx-auto px-2 md:px-2">
          <div className="flex items-center justify-between py-3">
            {/* Logo - Left */}
            <Link to="/" className="flex items-center">
              <img
                src="https://tdjwqdpgnmzhfcqsfyjh.supabase.co/storage/v1/object/sign/images/logo/RT-LOGO-Navigation.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8wYzVlMGM1NS1lYzI2LTRkMTYtOTc2MS1iZDQ1MWJhMGJiYjEiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvbG9nby9SVC1MT0dPLU5hdmlnYXRpb24ucG5nIiwiaWF0IjoxNzY3NjgzODA2LCJleHAiOjIwODMwNDM4MDZ9.UnTP2GD3PLPeHNBUE18hmLQHQbtbEKr6FRFOtAP3Cvc"
                alt="Raintree Chinese Medicine"
                className="h-8 w-auto"
              />
            </Link>

            {/* Button Group - Right */}
            <div className="flex items-center space-x-2 md:space-x-2">

            <Button
                onClick={onBookingClick}
                size="sm"
                className="h-10 w-10 md:h-8 md:w-16 rounded-full bg-white hover:bg-white hover:text-[#DBB981] text-black px-2 py-2 transition-colors inline-flex items-center justify-center"
              >
                <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                <span className="sr-only">Book</span>
              </Button>

              <Button
                asChild
                variant="outline"
                size="sm"
                className="h-10 w-10 md:h-8 md:w-16 rounded-full border border-[#FFF] text-[#FFF] bg-transparent hover:bg-transparent hover:text-[#DBB981] px-2 py-2 transition-colors inline-flex items-center justify-center"
              >
                <a href="tel:+61752409192" onClick={() => trackEvent('call_click', { location: 'nav' })}>
                  <Phone className="h-4 w-4" aria-hidden="true" />
                  <span className="sr-only">Call</span>
                </a>
              </Button>

              {/* User Account Button */}
              <Button
                onClick={onLoginClick}
                variant="ghost"
                size="sm"
                className="h-10 w-10 md:h-8 md:w-8 rounded-full pl-0 pr-0 text-white hover:bg-white/10 hover:text-[#DBB981] transition-colors"
                aria-label="My Account"
              >
                <CircleUserRound className="h-8 w-8 md:h-8 md:w-8" strokeWidth={0.75} />
              </Button>
              
              {/* Navigation menu for sm screens (right-side Sheet) */}
              <Sheet open={isDrawerOpen} onOpenChange={(open) => {
                setIsDrawerOpen(open)
                if (!open) {
                  setIsWhatWeHelpWithMobileOpen(false)
                  setIsClinicsMobileOpen(false)
                }
              }}>
                <SheetTrigger asChild>
                  <button
                    className="w-8 md:w-12 h-8 flex items-center justify-center text-white bg-transparent hover:bg-transparent focus:outline-none focus:ring-0 ml-0"
                    aria-label="Open menu"
                  >
                    <Menu className="h-5 w-5 text-white" />
                  </button>
                </SheetTrigger>
                
                <SheetContent side="right" className="bg-white w-full max-w-[350px] rounded-l-xl rounded-r-none" overlayClassName="bg-black/30">
                  <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                  <div className="p-4">
                    {/* Logo - Left */}
                    <Link to="/" className="flex items-center">
                      <div className="flex items-center">
                        <img
                          src="https://tdjwqdpgnmzhfcqsfyjh.supabase.co/storage/v1/object/sign/images/logo/RT-LOGO-Nav-Drawer.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8wYzVlMGM1NS1lYzI2LTRkMTYtOTc2MS1iZDQ1MWJhMGJiYjEiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvbG9nby9SVC1MT0dPLU5hdi1EcmF3ZXIucG5nIiwiaWF0IjoxNzY3Njg0NTU2LCJleHAiOjIwODMwNDQ1NTZ9.DrqkzdgZ88nNmjGwygA31fOlltomy3YqwoMO0ztDw48"
                          alt="Raintree Chinese Medicine"
                          className="h-8 w-auto"
                        />
                      </div>
                    </Link>
                    <div className="mt-4 border-t border-gray-200 mb-6" />
                    <div className="space-y-1">
                      {fullMenuLinks.map((item) => {
                        if (item.href === "/what-we-help-with") {
                          return (
                            <div key={item.href}>
                              <button
                                onClick={() => setIsWhatWeHelpWithMobileOpen(!isWhatWeHelpWithMobileOpen)}
                                className="w-full text-left text-gray-800 hover:text-[#DBB981] transition-colors p-3 rounded-md hover:bg-gray-50 flex items-center justify-between"
                              >
                                <span>{item.label}</span>
                                <ChevronRight className={cn("h-5 w-5 flex-shrink-0 transition-transform", isWhatWeHelpWithMobileOpen ? "rotate-90" : "")} />
                              </button>
                              {isWhatWeHelpWithMobileOpen && (
                                <div className="ml-4 mt-1 space-y-1">
                                  <Link
                                    to="/what-we-help-with"
                                    className="block text-gray-700 hover:text-[#DBB981] transition-colors p-3 rounded-md hover:bg-gray-50 text-sm"
                                    onClick={() => setIsDrawerOpen(false)}
                                  >
                                    Overview
                                  </Link>
                                  {whatWeHelpWithPages.map((page) => (
                                    <Link
                                      key={page.href}
                                      to={page.href}
                                      state={{ fromParent: false }}
                                      className="block text-gray-700 hover:text-[#DBB981] transition-colors p-3 rounded-md hover:bg-gray-50 text-sm"
                                      onClick={() => setIsDrawerOpen(false)}
                                    >
                                      {page.label}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          )
                        }
                        if (item.href === "/clinics") {
                          return (
                            <div key={item.href}>
                              <button
                                onClick={() => setIsClinicsMobileOpen(!isClinicsMobileOpen)}
                                className="w-full text-left text-gray-800 hover:text-[#DBB981] transition-colors p-3 rounded-md hover:bg-gray-50 flex items-center justify-between"
                              >
                                <span>{item.label}</span>
                                <ChevronRight className={cn("h-5 w-5 flex-shrink-0 transition-transform", isClinicsMobileOpen ? "rotate-90" : "")} />
                              </button>
                              {isClinicsMobileOpen && (
                                <div className="ml-4 mt-1 space-y-1">
                                  {clinicsPages.map((page) => (
                                    <Link
                                      key={page.href}
                                      to={page.href}
                                      state={{ fromParent: false }}
                                      className="block text-gray-700 hover:text-[#DBB981] transition-colors p-3 rounded-md hover:bg-gray-50 text-sm"
                                      onClick={() => setIsDrawerOpen(false)}
                                    >
                                      {page.label}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          )
                        }
                        return item.type === "route" ? (
                          <Link
                            key={item.href}
                            to={item.href}
                            className="block text-gray-800 hover:text-[#DBB981] transition-colors p-3 rounded-md hover:bg-gray-50"
                            onClick={() => setIsDrawerOpen(false)}
                          >
                            {item.label}
                          </Link>
                        ) : (
                          <button
                            key={item.href}
                            onClick={() => handleNavClick(item.href)}
                            className="w-full text-left text-gray-800 hover:text-[#DBB981] transition-colors p-3 rounded-md hover:bg-gray-50"
                          >
                            {item.label}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Flow Modal/Drawer */}
      {isMobile ? (
        <Drawer open={isBookingOpen} onOpenChange={handleBookingClose}>
          <DrawerContent className="h-[90vh] flex flex-col">
            <DrawerTitle className="sr-only">Book Appointment</DrawerTitle>
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
              <h2 className="text-lg font-normal text-[#3f4141]">Book Appointment</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleBookingClose(false)}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <BookingFlowContent
              className="flex-1 overflow-hidden"
              mode={bookingMode}
              onClose={() => handleBookingClose(false)}
              onComplete={(janeUrl) => {
                window.open(janeUrl, '_blank')
                handleBookingClose(false)
              }}
            />
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={isBookingOpen} onOpenChange={handleBookingClose}>
          <DialogContent className="max-w-lg w-full h-[70vh] p-0 rounded-xl overflow-hidden flex flex-col">
            <DialogHeader className="p-4 border-b flex-shrink-0">
              <DialogTitle className="text-lg font-normal text-[#3f4141]">Book Appointment</DialogTitle>
            </DialogHeader>
            <BookingFlowContent
              className="flex-1 overflow-hidden"
              mode={bookingMode}
              onClose={() => handleBookingClose(false)}
              onComplete={(janeUrl) => {
                window.open(janeUrl, '_blank')
                handleBookingClose(false)
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* My Account Modal */}
      {isMobile ? (
        <Drawer open={isLoginOpen} onOpenChange={setIsLoginOpen}>
          <DrawerContent className="flex flex-col">
            <div className="flex flex-col">
              <DrawerTitle className="sr-only">My Account</DrawerTitle>
              <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
                <h2 className="text-lg font-light text-[#3f4141]">My Account</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsLoginOpen(false)}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <LoginIframeContent onClose={() => setIsLoginOpen(false)} />
            </div>
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={isLoginOpen} onOpenChange={setIsLoginOpen}>
          <DialogContent className="max-w-md w-full p-0 rounded-lg">
            <div className="flex flex-col">
              <DialogHeader className="p-4 border-b">
                <DialogTitle className="font-light text-[#3f4141]">My Account</DialogTitle>
              </DialogHeader>
              <LoginIframeContent onClose={() => setIsLoginOpen(false)} />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}